import { Eth } from 'web3-eth'

import { SMART_CONTRACT_CONFIG } from '@/shared/SmartContractConfig'
import { extractSignature } from '@/shared/getFunctionSignature'
import { DecodedValue } from '@/popup/compositions/useABIDecoder'

const KEY_MANAGER_EXECUTE_SELECTOR = SMART_CONTRACT_CONFIG.kmExecuteHex
const KEY_MANAGER_EXECUTE_PARAMS = SMART_CONTRACT_CONFIG.kmExecuteParameters

const KEY_MANAGER_EXECUTE_RELAY_CALL_SELECTOR =
  SMART_CONTRACT_CONFIG.kmExecuteRelayCallHex
const KEY_MANAGER_EXECUTE_RELAY_CALL_PARAMS =
  SMART_CONTRACT_CONFIG.kmExecuteRelayCallParameters

const UP_EXECUTE_SELECTOR = SMART_CONTRACT_CONFIG.upExecuteHex
const UP_EXECUTE_PARAMS = SMART_CONTRACT_CONFIG.upExecuteParameters

const getDecodedParameters = (web3Eth: Eth, selector: string, data: string) => {
  const component = SMART_CONTRACT_CONFIG.componentSelectors.find(
    componentItem => componentItem.selector === selector
  )
  if (component && component.functionSignature) {
    const { params } = extractSignature(component.functionSignature)
    return web3Eth.abi.decodeParameters(params, data.slice(10))
  }
}

/**
 * Function to unwrap tx bytes from a call done through the browser extension (KeyManager -> Universal Profile -> End contract)
 *
 * @param web3Eth - web3 instance used to decodeParameters
 * @param inputData - the transaction input bytes
 *
 * @returns {data: string, value?: string} - the unwrapped bytes sent to the end transaction, that have been through the key manager, and the UP, as well as the value of tx
 */
export const unwrapData = (
  web3Eth: Eth,
  inputData?: string
): {
  data: string
  value?: string
  decodedFunctionCall?: DecodedValue
} => {
  let data = inputData,
    value
  if (!data || data === '0x') {
    return { data: '0x' }
  }
  let decodedFunctionCall: DecodedValue = {}

  // First layer of unwrapping
  switch (
    data.slice(0, 10) // We extract the selector on the inputData
  ) {
    case KEY_MANAGER_EXECUTE_SELECTOR: {
      // execute function from LSP6KeyManager contract
      decodedFunctionCall = web3Eth.abi.decodeParameters(
        KEY_MANAGER_EXECUTE_PARAMS,
        data.slice(10)
      )
      data = decodedFunctionCall['payload']
      break
    }
    case KEY_MANAGER_EXECUTE_RELAY_CALL_SELECTOR: {
      // executeRelayCall function from LSP6KeyManager contract
      decodedFunctionCall = web3Eth.abi.decodeParameters(
        KEY_MANAGER_EXECUTE_RELAY_CALL_PARAMS,
        data.slice(10)
      )
      data = decodedFunctionCall['payload']
      break
    }
    case UP_EXECUTE_SELECTOR: {
      // execute function from ERC725X contract
      decodedFunctionCall = web3Eth.abi.decodeParameters(
        UP_EXECUTE_PARAMS,
        data.slice(10)
      )
      data = decodedFunctionCall['data']
      value = decodedFunctionCall['value']
      break
    }
    // Missing decoding for `case UP_EXECUTE_BATCH_SELECTOR`
    default: {
      decodedFunctionCall =
        getDecodedParameters(web3Eth, data.slice(0, 10), data) ?? {}
      if (decodedFunctionCall && decodedFunctionCall['bytes']) {
        data = decodedFunctionCall['bytes']
      }
    }
  }

  if (typeof data !== 'string' || data.length === 0) {
    return { data: '0x', value, decodedFunctionCall }
  }

  // What's the purpose of this second layer?
  // Second layer of unwrapping
  if (data.length > 10 && data.slice(0, 10) === UP_EXECUTE_SELECTOR) {
    decodedFunctionCall = web3Eth.abi.decodeParameters(
      UP_EXECUTE_PARAMS,
      data.slice(10)
    )
    data = decodedFunctionCall['data']
    value = decodedFunctionCall['value']
  }

  if (typeof data !== 'string' || data.length === 0) {
    return { data: '0x', value, decodedFunctionCall }
  }

  return { data: data === '' ? '0x' : data, value, decodedFunctionCall }
}
